/**
 * GstSummaryView.tsx — GST Rate-wise Tax Summary
 * Shows taxable amount, CGST, SGST, IGST, CESS broken down by GST rate slab
 * (0%, 5%, 12%, 18%, 28%) — both for Sales (output) and Purchases (input/ITC).
 */
import { useMemo } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';

const fmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GST_SLABS = [0, 5, 12, 18, 28];

export const GstSummaryView = ({ onQuit }: any) => {
  const activeCompany = useStore((s) => s.activeCompany);
  const fromDate      = useStore((s) => s.fromDate);
  const toDate        = useStore((s) => s.toDate);

  const data = useLiveQuery(async () => {
    if (!activeCompany) return null;

    const allTypes = ['Sales', 'Credit Note', 'Purchase', 'Debit Note'];
    const vouchers = await db.vouchers
      .where('companyId').equals(activeCompany.id!)
      .filter(v => allTypes.includes(v.type) && v.date >= fromDate && v.date <= toDate && !v.deleted)
      .toArray();

    const ledgers    = await db.ledgers.where('companyId').equals(activeCompany.id!).toArray();
    const ledgerMap  = new Map<number, any>(ledgers.map(l => [l.id!, l]));
    const groups     = await db.groups.where('companyId').equals(activeCompany.id!).toArray();
    const groupMap   = new Map<number, string>(groups.map(g => [g.id!, g.name]));
    const stockItems = await db.stockItems.where('companyId').equals(activeCompany.id!).toArray();
    const stockItemMap = new Map<number, any>(stockItems.map(s => [s.id!, s]));

    const invEntries = await db.inventoryEntries
      .where('voucherId').anyOf(vouchers.map(v => v.id!)).toArray();
    const invByVoucher = new Map<number, typeof invEntries>();
    invEntries.forEach(ie => {
      if (!invByVoucher.has(ie.voucherId)) invByVoucher.set(ie.voucherId, []);
      invByVoucher.get(ie.voucherId)!.push(ie);
    });

    const entries = await db.voucherEntries
      .where('voucherId').anyOf(vouchers.map(v => v.id!)).toArray();
    const entriesByVoucher = new Map<number, typeof entries>();
    entries.forEach(e => {
      if (!entriesByVoucher.has(e.voucherId)) entriesByVoucher.set(e.voucherId, []);
      entriesByVoucher.get(e.voucherId)!.push(e);
    });

    // rate-slab accumulators: sales & purchase
    type SlabData = { taxable: number; cgst: number; sgst: number; igst: number; cess: number; count: number };
    const salesSlabs:    Record<number, SlabData> = {};
    const purchaseSlabs: Record<number, SlabData> = {};
    GST_SLABS.forEach(s => {
      salesSlabs[s]    = { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, count: 0 };
      purchaseSlabs[s] = { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, count: 0 };
    });
    // also an 'other' bucket
    const salesOther    = { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, count: 0 };
    const purchaseOther = { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, count: 0 };

    vouchers.forEach(v => {
      const vEntries = entriesByVoucher.get(v.id!) || [];
      const vInv     = invByVoucher.get(v.id!) || [];
      const isOut    = v.type === 'Sales' || v.type === 'Credit Note';
      const isIn     = v.type === 'Purchase' || v.type === 'Debit Note';

      let cgst = 0, sgst = 0, igst = 0, cess = 0, taxable = 0;
      vEntries.forEach(e => {
        const l = ledgerMap.get(e.ledgerId);
        if (!l) return;
        const grp = l.groupId ? groupMap.get(l.groupId) : '';
        const amt = e.amount || 0;
        if (l.name === 'CGST') cgst += amt;
        else if (l.name === 'SGST') sgst += amt;
        else if (l.name === 'IGST') igst += amt;
        else if (l.name === 'CESS') cess += amt;
        else if (['Sales Accounts', 'Direct Incomes', 'Indirect Incomes'].includes(grp || '') && isOut) taxable += amt;
        else if (['Purchase Accounts', 'Direct Expenses'].includes(grp || '') && isIn) taxable += amt;
      });

      if (taxable === 0) return;

      // Determine effective rate from inventory items (weighted)
      let dominant = -1;
      if (vInv.length > 0) {
        const rateAmt: Record<number, number> = {};
        vInv.forEach(ie => {
          const si   = ie.stockItemId ? stockItemMap.get(ie.stockItemId) : null;
          const rate = si?.gstRate ?? -1;
          rateAmt[rate] = (rateAmt[rate] || 0) + (ie.quantity * ie.rate);
        });
        const topEntry = Object.entries(rateAmt).sort((a, b) => b[1] - a[1])[0];
        if (topEntry) dominant = parseInt(topEntry[0]);
      }
      // Fallback from tax amounts
      if (dominant < 0 && taxable > 0) {
        const totalTax = cgst + sgst + igst;
        if (totalTax > 0) dominant = Math.round((totalTax / taxable) * 100);
      }

      const slabs = isOut ? salesSlabs : purchaseSlabs;
      const other = isOut ? salesOther : purchaseOther;
      if (slabs[dominant]) {
        slabs[dominant].taxable += taxable;
        slabs[dominant].cgst    += cgst;
        slabs[dominant].sgst    += sgst;
        slabs[dominant].igst    += igst;
        slabs[dominant].cess    += cess;
        slabs[dominant].count   += 1;
      } else {
        other.taxable += taxable; other.cgst += cgst; other.sgst += sgst;
        other.igst += igst; other.cess += cess; other.count += 1;
      }
    });

    return { salesSlabs, purchaseSlabs, salesOther, purchaseOther };
  }, [activeCompany, fromDate, toDate]);

  if (!activeCompany) return null;

  const th = 'px-2 py-1.5 text-left font-bold text-[11px] border-b border-[#aebec8] whitespace-nowrap';
  const td = 'px-2 py-[3px] border-b border-gray-100 text-[11px] whitespace-nowrap';
  const tR = `${td} text-right tabular-nums`;

  const SlabTable = ({ slabs, other, title, color }: any) => {
    const allRows = [
      ...GST_SLABS.map(r => ({ rate: `${r}%`, ...slabs[r], isGst: r > 0 })),
      { rate: 'Other', ...other, isGst: true },
    ].filter(r => r.taxable > 0 || r.cgst > 0 || r.igst > 0);

    const totals = allRows.reduce(
      (acc, r) => ({
        taxable: acc.taxable + r.taxable, cgst: acc.cgst + r.cgst,
        sgst: acc.sgst + r.sgst, igst: acc.igst + r.igst,
        cess: acc.cess + (r.cess || 0), count: acc.count + r.count,
      }),
      { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, count: 0 }
    );

    return (
      <div className="mb-4">
        <div className={`px-3 py-1.5 font-bold text-[12px] ${color} text-white`}>{title}</div>
        {allRows.length === 0 ? (
          <div className="px-3 py-4 text-center text-gray-400 text-sm">No data for this period.</div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>GST Rate</th>
                <th className={`${th} text-center`}>Vouchers</th>
                <th className={`${th} text-right`}>Taxable Value (₹)</th>
                <th className={`${th} text-right`}>CGST (₹)</th>
                <th className={`${th} text-right`}>SGST (₹)</th>
                <th className={`${th} text-right`}>IGST (₹)</th>
                <th className={`${th} text-right`}>CESS (₹)</th>
                <th className={`${th} text-right`}>Total Tax (₹)</th>
                <th className={`${th} text-right`}>Grand Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {allRows.map((r, i) => {
                const totalTax = r.cgst + r.sgst + r.igst + (r.cess || 0);
                return (
                  <tr key={r.rate} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8fcfc]'}>
                    <td className={`${td} font-semibold`}>
                      <span className="px-2 py-0.5 rounded text-[10px] bg-teal-50 text-teal-700 border border-teal-200">
                        {r.rate}
                      </span>
                    </td>
                    <td className={`${td} text-center text-gray-500`}>{r.count}</td>
                    <td className={tR}>{fmt(r.taxable)}</td>
                    <td className={tR}>{r.cgst > 0 ? fmt(r.cgst) : '—'}</td>
                    <td className={tR}>{r.sgst > 0 ? fmt(r.sgst) : '—'}</td>
                    <td className={tR}>{r.igst > 0 ? fmt(r.igst) : '—'}</td>
                    <td className={tR}>{r.cess > 0 ? fmt(r.cess) : '—'}</td>
                    <td className={`${tR} font-semibold`}>{fmt(totalTax)}</td>
                    <td className={`${tR} font-bold`}>{fmt(r.taxable + totalTax)}</td>
                  </tr>
                );
              })}
              <tr className="bg-[#f0fafa] font-bold border-t-2 border-[#aebec8]">
                <td className={th}>Total</td>
                <td className={`${th} text-center`}>{totals.count}</td>
                <td className={`${th} text-right`}>{fmt(totals.taxable)}</td>
                <td className={`${th} text-right`}>{fmt(totals.cgst)}</td>
                <td className={`${th} text-right`}>{fmt(totals.sgst)}</td>
                <td className={`${th} text-right`}>{fmt(totals.igst)}</td>
                <td className={`${th} text-right`}>{totals.cess > 0 ? fmt(totals.cess) : '—'}</td>
                <td className={`${th} text-right`}>{fmt(totals.cgst + totals.sgst + totals.igst + totals.cess)}</td>
                <td className={`${th} text-right text-[#1e8585]`}>{fmt(totals.taxable + totals.cgst + totals.sgst + totals.igst + totals.cess)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
        <span className="w-1/3">GST Rate-wise Tax Summary</span>
        <span className="w-1/3 text-center">{activeCompany.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={onQuit}>✕</span>
      </div>

      <div className="bg-white px-3 py-1.5 font-bold border-b border-[#aebec8] shadow-sm shrink-0 text-[11px]">
        Period: {fromDate} to {toDate}
        <span className="ml-4 font-normal text-gray-500">Breakup of GST liability and ITC by rate slab (5% / 12% / 18% / 28%)</span>
      </div>

      <div className="flex-1 overflow-auto p-2">
        {!data ? (
          <div className="flex items-center justify-center h-32 text-gray-400">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1e8585] mr-2"></div>
            Computing GST summary...
          </div>
        ) : (
          <>
            <SlabTable
              slabs={data.salesSlabs} other={data.salesOther}
              title="Output Tax — Sales & Credit Notes (GST Liability)"
              color="bg-[#1e8585]"
            />
            <SlabTable
              slabs={data.purchaseSlabs} other={data.purchaseOther}
              title="Input Tax Credit — Purchases & Debit Notes (ITC Available)"
              color="bg-[#2a6464]"
            />
            <div className="text-[10px] text-gray-400 px-2 py-1 border-t border-gray-100">
              ⚠ Rate classification uses the dominant GST rate from stock items on each voucher.
              Vouchers without stock items are classified from the effective tax percentage.
              Review GSTR-1 and GSTR-3B views for portal-ready data.
            </div>
          </>
        )}
      </div>
    </div>
  );
};
